import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AppShell } from "./components/AppShell";
import { AuthGuard } from "./lib/auth";
import { ApprovalsPage } from "./pages/Approvals";
import { CustomerDetailPage } from "./pages/CustomerDetail";
import { CustomersPage } from "./pages/Customers";
import { LogsPage } from "./pages/Logs";
import { LoginPage } from "./pages/Login";
import { MissionControlPage } from "./pages/MissionControl";
import { ProjectsPage } from "./pages/Projects";
import { ProjectWizardPage } from "./pages/ProjectWizard";
import { RuleBuilderPage } from "./pages/RuleBuilder";
import { RulesPage } from "./pages/Rules";
import {
  AppearanceSettingsTab,
  ProfileSettingsTab,
  WhitelistSettingsTab,
} from "./pages/settings-tabs";
import {
  ProjectIntegrationsTab,
  ProjectOverviewTab,
  ProjectQualityTab,
  ProjectRulesTab,
  ProjectSettingsTab,
  ProjectTrackingTab,
} from "./pages/project-tabs";
import {
  ProjectConsentTab,
  ProjectDestinationsTab,
} from "./pages/project-tracking-tabs";
import { ProjectImportTab } from "./pages/project-import-tab";
import { NotFoundPage } from "./pages/NotFound";
import { ProjectDetailLayout } from "./pages/ProjectDetail";
import { SettingsLayout } from "./pages/Settings";
import { WebhookEndpointDetailPage } from "./pages/WebhookEndpointDetail";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <AuthGuard>
                <AppShell />
              </AuthGuard>
            }
          >
            <Route index element={<MissionControlPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/new" element={<ProjectWizardPage />} />
            <Route path="projects/:id" element={<ProjectDetailLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<ProjectOverviewTab />} />
              <Route path="tracking" element={<ProjectTrackingTab />} />
              <Route
                path="domains"
                element={<Navigate to="../tracking" replace />}
              />
              <Route
                path="integrations"
                element={<ProjectIntegrationsTab />}
              />
              <Route path="consent" element={<ProjectConsentTab />} />
              <Route path="destinations" element={<ProjectDestinationsTab />} />
              <Route path="import" element={<ProjectImportTab />} />
              <Route path="quality" element={<ProjectQualityTab />} />
              <Route path="rules" element={<ProjectRulesTab />} />
              <Route path="settings" element={<ProjectSettingsTab />} />
              <Route
                path="danger"
                element={<Navigate to="../settings" replace />}
              />
            </Route>
            <Route
              path="projects/:id/webhook-endpoints/:endpointId"
              element={<WebhookEndpointDetailPage />}
            />
            <Route path="customers" element={<CustomersPage />} />
            <Route
              path="customers/:projectId/:visitorId"
              element={<CustomerDetailPage />}
            />
            <Route path="logs" element={<LogsPage />} />
            <Route path="rules" element={<RulesPage />} />
            <Route path="rules/new" element={<RuleBuilderPage />} />
            <Route path="rules/:id" element={<RuleBuilderPage />} />
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="whitelist" replace />} />
              <Route path="whitelist" element={<WhitelistSettingsTab />} />
              <Route path="profile" element={<ProfileSettingsTab />} />
              <Route path="appearance" element={<AppearanceSettingsTab />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  );
}
